import type { AABB } from '../../geo/types';

/**
 * Items filed under every cell of a square grid their bounds cross, as SpatialHash files them,
 * so a query meets the same items; lot planning asks a quarter of a million times whether any
 * of them clashes, and numeric keys with no de-duplication answer that at a fraction of the cost.
 */
export interface CellGrid<T> {
  insert(item: T, box: AABB): void;
  /** Whether `test` holds for an item filed in a cell the box crosses; it may meet one twice. */
  some(box: AABB, test: (item: T) => boolean): boolean;
  /** The items filed in the cell holding a point, in filing order. */
  at(x: number, y: number): readonly T[];
}

/* Packs a cell's column and row into one key; the site spans a few thousand cells each way. */
const STRIDE = 1_000_003;

export function createCellGrid<T>(cellSize: number): CellGrid<T> {
  const cells = new Map<number, T[]>();

  function insert(item: T, box: AABB): void {
    for (let cx = Math.floor(box.minX / cellSize); cx <= Math.floor(box.maxX / cellSize); cx++) {
      for (let cy = Math.floor(box.minY / cellSize); cy <= Math.floor(box.maxY / cellSize); cy++) {
        const key = cx * STRIDE + cy;
        const list = cells.get(key);
        if (list) list.push(item);
        else cells.set(key, [item]);
      }
    }
  }

  function some(box: AABB, test: (item: T) => boolean): boolean {
    for (let cx = Math.floor(box.minX / cellSize); cx <= Math.floor(box.maxX / cellSize); cx++) {
      for (let cy = Math.floor(box.minY / cellSize); cy <= Math.floor(box.maxY / cellSize); cy++) {
        const list = cells.get(cx * STRIDE + cy);
        if (list && list.some(test)) return true;
      }
    }
    return false;
  }

  const none: readonly T[] = [];

  function at(x: number, y: number): readonly T[] {
    return cells.get(Math.floor(x / cellSize) * STRIDE + Math.floor(y / cellSize)) ?? none;
  }

  return { insert, some, at };
}
